// Dependencies
import { FC, useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Apis
import apis from '../../../apis';
import { AppContext } from '../../../context';
import { BasicInfo } from './BasicInfo';
import { LayoutInfo } from './LayoutInfo';
import { useToastContext } from '../../../components/Toast/toastContext';
import { ROUTES } from '../../../contants';
import { ITemplate } from '../../../resource';

/** Client-side payload for PUT — server also whitelists; never send populated docs. */
function buildTemplateSavePayload(template: ITemplate | undefined, isAdmin: boolean) {
  if (!template) return {};
  const userIds = (template.userIds || [])
    .map((u) => {
      if (typeof u === 'string') return u;
      return u?.id || (u as { _id?: string })?._id;
    })
    .filter(Boolean);

  const themeId =
    typeof template.theme === 'string' ? template.theme : template.theme?._id || null;

  const payload: Record<string, unknown> = {
    name: template.name,
    description: template.description,
    thumbnail_url: template.thumbnail_url,
    theme: themeId,
    color: template.color,
    colorSets: template.colorSets,
    userIds,
    icon_url: template.icon_url,
    fond: template.fond,
    manImageUrl: template.manImageUrl,
  };

  if (isAdmin) {
    payload.isPublished = !!template.isPublished;
    payload.isDeleted = !!template.isDeleted;
  }

  return payload;
}

// Export template detail page
export const TemplateDetailPage: FC<{ isNew?: boolean }> = ({ isNew }) => {
  const [template, setTemplate] = useState<ITemplate>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);

  const { isAdmin } = useContext(AppContext);
  const navigate = useNavigate();
  const { activeToast } = useToastContext();
  const { id } = useParams<{ id: string }>();

  const onSaveTemplate = async () => {
    if (isSaving) return;
    setIsSaving(true);
    const payload = buildTemplateSavePayload(template, !!isAdmin);

    try {
      if (isNew) {
        await apis.template.createTemplate(payload);
        activeToast({
          type: 'success',
          text: 'Template created successfully!',
        });
        navigate(ROUTES.TEMPLATE.LIST);
        return;
      }

      if (id) {
        const res = await apis.template.updateTemplate(id, payload);
        setTemplate(res);
        activeToast({
          type: 'success',
          text: 'Template saved.',
        });
      }
    } catch (err: any) {
      console.log(err);
      activeToast({
        type: 'error',
        text: err?.response?.data?.msg || err?.message || 'Failed to save template.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onUpdateTemplate = (value: Partial<ITemplate>) =>
    setTemplate((prev) => ({ ...(prev as ITemplate), ...value }));

  const fetchTemplate = () => {
    if (!id) return;
    setIsLoading(true);
    apis.template
      .getTemplate(id)
      .then((res) => {
        setTemplate(res);
      })
      .catch((err) => {
        console.log(err);
        activeToast({
          type: 'error',
          text: 'Failed to load template.',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (id && !isNew) {
      fetchTemplate();
    } else {
      setIsLoading(false);
    }
  }, [id, isNew]);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={40}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack divider={<Divider sx={{ mt: 16, mb: 16 }} />}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={12}>
        <Typography variant="h4">
          {isNew ? 'Create' : 'Edit'} Template
        </Typography>
        <Button onClick={onSaveTemplate} disabled={isSaving || (!isNew && !template)}>
          {isSaving ? 'Saving…' : 'Save Template'}
        </Button>
      </Stack>
      <Stack>
        {(isNew || template) && <BasicInfo template={template} onChange={onUpdateTemplate} />}
      </Stack>
      <Stack>
        {!isNew && template && <LayoutInfo template={template} />}
      </Stack>
    </Stack>
  );
};
